import { useState, useEffect } from 'react';
import api from '../api/axios';
import toast from 'react-hot-toast';
import { MdAdd, MdClose, MdDelete, MdEdit, MdToggleOn, MdToggleOff } from 'react-icons/md';
import DragDropImageUpload from '../components/DragDropImageUpload';
import Pagination from '../components/Pagination';
import usePagination from '../hooks/usePagination';

const emptyForm = { title: '', summary: '', image_url: '', button_text: '', button_link: '', is_active: false };

export default function StickyNews() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [deleteModal, setDeleteModal] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const { page, limit, setPage, setLimit } = usePagination(20);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });

  const fetchItems = async (fetchPage = page, fetchLimit = limit) => {
    window.scrollTo(0, 0);
    try {
      const res = await api.get('/admin/sticky-news', { params: { page: fetchPage, limit: fetchLimit } });
      setItems(res.data.data);
      setPagination(res.data.pagination || { page: 1, pages: 1, total: 0 });
    } catch (err) {
      toast.error('Failed to load sticky news');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems(page, limit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit]);

  const set = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  const resetForm = () => {
    setForm(emptyForm);
    setEditingId(null);
    setShowForm(false);
  };

  const startEdit = (item) => {
    setForm({
      title: item.title || '',
      summary: item.summary || '',
      image_url: item.image_url || '',
      button_text: item.button_text || '',
      button_link: item.button_link || '',
      is_active: item.is_active || false,
    });
    setEditingId(item.id);
    setShowForm(true);
    window.scrollTo(0, 0);
  };

  const handleUpload = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    setUploading(true);
    try {
      const res = await api.post('/admin/upload/sticky-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      set('image_url', res.data.data.url);
      toast.success('Image uploaded');
    } catch (err) {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.summary.trim()) {
      return toast.error('Title and summary are required');
    }
    try {
      if (editingId) {
        await api.put(`/admin/sticky-news/${editingId}`, form);
        toast.success('Sticky news updated');
      } else {
        await api.post('/admin/sticky-news', form);
        toast.success('Sticky news created');
      }
      resetForm();
      setPage(1);
      fetchItems(1, limit);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed');
    }
  };

  const toggleItem = async (id) => {
    try {
      await api.patch(`/admin/sticky-news/${id}/toggle`);
      fetchItems();
    } catch (err) {
      toast.error('Toggle failed');
    }
  };

  const confirmDelete = async () => {
    if (!deleteModal) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/sticky-news/${deleteModal.id}`);
      toast.success('Sticky news deleted');
      setDeleteModal(null);
      fetchItems();
    } catch (err) {
      toast.error('Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) return <div className="page-loader">Loading...</div>;

  return (
    <div className="page">
      <div className="page-header">
        <h2>Sticky News</h2>
        <p className="text-muted">The one-time announcement card shown to every device the first time it opens the app — a new active card is shown again to everyone, once each.</p>
      </div>

      <div className="page-header" style={{ paddingTop: 0 }}>
        <span />
        <button className="btn btn-primary" onClick={() => (showForm ? resetForm() : setShowForm(true))}>
          {showForm ? <><MdClose /> Cancel</> : <><MdAdd /> Create Sticky News</>}
        </button>
      </div>

      {showForm && (
        <form className="card form-card" onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Title</label>
            <input value={form.title} onChange={e => set('title', e.target.value)} placeholder="e.g. Update your app" />
          </div>
          <div className="form-group">
            <label>Summary</label>
            <textarea value={form.summary} onChange={e => set('summary', e.target.value)} placeholder="e.g. New features are here! Update now for the best experience." rows={3} />
          </div>
          <div className="form-group">
            <label>Image</label>
            <DragDropImageUpload
              type="image"
              uploading={uploading}
              imageUrl={form.image_url}
              onFileSelect={handleUpload}
              label="Click to select an image (e.g. your logo)"
            />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Button Text (optional)</label>
              <input value={form.button_text} onChange={e => set('button_text', e.target.value)} placeholder="e.g. Update Now" />
            </div>
            <div className="form-group">
              <label>Button Link (optional)</label>
              <input value={form.button_link} onChange={e => set('button_link', e.target.value)} placeholder="Play Store / App Store URL" />
            </div>
          </div>
          <label className="checkbox-label">
            <input type="checkbox" checked={form.is_active} onChange={e => set('is_active', e.target.checked)} />
            Active (shown to devices that haven't seen it yet — deactivates any other active sticky news)
          </label>
          <button type="submit" className="btn btn-primary mt-md">{editingId ? 'Save Changes' : 'Create Sticky News'}</button>
        </form>
      )}

      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th className="hide-mobile">Image</th>
              <th>Title</th>
              <th>Button</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map(item => (
              <tr key={item.id}>
                <td className="hide-mobile" style={{ textAlign: 'center', padding: '8px' }}>
                  {item.image_url && <img src={item.image_url} alt={item.title} style={{ width: '56px', height: '56px', objectFit: 'contain', borderRadius: '8px' }} />}
                </td>
                <td>
                  <span className="article-title">{item.title}</span>
                  <p className="cell-sub">{item.summary}</p>
                </td>
                <td>{item.button_text || '—'}</td>
                <td>
                  <span className={`badge ${item.is_active ? 'badge-live' : 'badge-expired'}`}>{item.is_active ? 'Active' : 'Inactive'}</span>
                </td>
                <td>
                  <div className="action-btns">
                    <button className="btn btn-sm btn-ghost" onClick={() => toggleItem(item.id)} title={item.is_active ? 'Deactivate' : 'Activate'}>
                      {item.is_active ? <MdToggleOn className="text-green" /> : <MdToggleOff />}
                    </button>
                    <button className="btn btn-sm btn-ghost" onClick={() => startEdit(item)} title="Edit"><MdEdit /></button>
                    <button className="btn btn-sm btn-danger" onClick={() => setDeleteModal(item)} title="Delete"><MdDelete /></button>
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan="5" className="empty-row">No sticky news yet</td></tr>}
          </tbody>
        </table>
      </div>

      <div className="mobile-cards hide-desktop">
        {items.map(item => (
          <div key={item.id} className="mobile-card">
            <div className="mobile-card-header">
              <span className={`badge ${item.is_active ? 'badge-live' : 'badge-expired'}`}>{item.is_active ? 'Active' : 'Inactive'}</span>
            </div>
            <h4>{item.title}</h4>
            <p className="text-muted">{item.summary}</p>
            <div className="action-btns">
              <button className="btn btn-sm btn-ghost" onClick={() => toggleItem(item.id)}>{item.is_active ? 'Deactivate' : 'Activate'}</button>
              <button className="btn btn-sm btn-ghost" onClick={() => startEdit(item)}><MdEdit /> Edit</button>
              <button className="btn btn-sm btn-danger" onClick={() => setDeleteModal(item)}><MdDelete /> Delete</button>
            </div>
          </div>
        ))}
      </div>

      {items.length > 0 && (
        <Pagination pagination={pagination} onPageChange={setPage} limit={limit} onLimitChange={setLimit} />
      )}

      {deleteModal && (
        <div className="modal-overlay" onClick={() => setDeleteModal(null)}>
          <div className="modal modal-sm" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Delete Sticky News</h3>
              <button className="modal-close" onClick={() => setDeleteModal(null)}><MdClose /></button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete this sticky news?</p>
              <p className="text-muted" style={{ marginTop: 8 }}>"{deleteModal.title}"</p>
              <p className="text-muted" style={{ marginTop: 8 }}>This action cannot be undone.</p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setDeleteModal(null)}>Cancel</button>
              <button className="btn btn-danger-solid" onClick={confirmDelete} disabled={deleting}>
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
